import { classNames } from "components/utils";
import React, { forwardRef } from "react";

export interface ButtonProps {
  /** Button text content */
  label: string;
  /** Click handler - optional when using hrefInNewTab */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Additional CSS classes */
  className?: string;
  /** Disable button interaction */
  disabled?: boolean;
  /** Icon to display before label */
  iconLeft?: React.ReactNode;
  /** Icon to display after label */
  iconRight?: React.ReactNode;
  /** Show loading spinner and disable interaction */
  loading?: boolean;
  /** Render as link that opens in new tab instead of button */
  hrefInNewTab?: string;
  /** Mouse out event handler */
  onMouseOut?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
  ) => Promise<void> | void;
  /** Mouse over event handler */
  onMouseOver?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
  ) => Promise<void> | void;
  /** Button size variant */
  size?: "large" | "medium" | "small";
  /** Inline styles to apply to button element */
  style?: React.CSSProperties;
  /** Visual style variant */
  type?: "primary" | "secondary" | "danger" | "warning" | "bold" | "nvidia";
  /** Test identifier for E2E and integration tests */
  "data-testid"?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      disabled = false,
      iconLeft,
      iconRight,
      label,
      loading,
      onClick = () => {},
      hrefInNewTab,
      className = "",
      onMouseOut = () => {},
      onMouseOver = () => {},
      size = undefined,
      style = {},
      type = "primary",
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    // const handleClick = async (
    //   e: React.MouseEvent<HTMLDivElement, MouseEvent>
    // ) => {
    //   await onClick(e);
    // };

    const getClasses = (extraClasses: string) => {
      // Base classes shared by all button variants
      const baseClasses =
        "inline-flex items-center px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 dark:focus:ring-offset-zinc-900";

      // Variant-specific classes
      const variantClasses = {
        primary:
          "border border-transparent text-white nvidia:text-black bg-highlightDarker hover:bg-highlight dark:hover:bg-highlightLighter focus:ring-highlightDarker",
        secondary:
          "border border-transparent text-gray-700 dark:text-secondary bg-white dark:bg-zinc-900 hover:bg-gray-50 dark:hover:bg-zinc-700 focus:ring-highlight",
        danger:
          "border border-transparent text-white bg-rose-500 dark:bg-rose-600 hover:bg-rose-600 dark:hover:bg-rose-500 focus:ring-rose-500",
        warning:
          "border border-transparent text-white bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500",
        bold: "border border-transparent text-white bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-gray-100 focus:ring-slate-700 dark:text-black",
        nvidia:
          "border border-transparent text-black bg-[#76B900] hover:bg-[#8ACC00] focus:ring-[#76B900]",
      };

      // State classes
      const stateClasses = disabled ? "opacity-50 pointer-events-none" : "";
      const sizeClasses = size === "small" ? "" : "py-2";

      // Combine all classes
      return classNames(
        baseClasses,
        variantClasses[type],
        stateClasses,
        sizeClasses,
        extraClasses,
      );
    };

    if (hrefInNewTab) {
      return (
        <a
          className={getClasses(className)}
          href={hrefInNewTab}
          target="_blank"
          rel="noopener noreferrer"
          style={style}
          data-testid={dataTestId}
        >
          {iconLeft && !loading && (
            <span className="mr-2 -ml-1 inline-flex" aria-hidden="true">
              {iconLeft}
            </span>
          )}
          {label}
          {iconRight && (
            <span className="ml-2 -mr-1 inline-flex" aria-hidden="true">
              {iconRight}
            </span>
          )}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        className={getClasses(className)}
        disabled={disabled || loading}
        onClick={onClick}
        style={style}
        data-testid={dataTestId}
        aria-busy={loading ? "true" : undefined}
        aria-disabled={disabled ? "true" : undefined}
      >
        {loading && (
          <>
            <svg
              className={`animate-spin -ml-1 mr-3 h-5 w-5 ${
                type === "nvidia"
                  ? "text-black"
                  : type === "primary"
                    ? "text-white nvidia:text-black"
                    : type === "secondary"
                      ? "text-gray-700 dark:text-secondary"
                      : "text-white"
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              role="status"
              aria-label="Loading"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </>
        )}
        {iconLeft && !loading && (
          <span className="mr-2 -ml-1 inline-flex" aria-hidden="true">
            {iconLeft}
          </span>
        )}
        {label}
        {iconRight && (
          <span className="ml-2 -mr-1 inline-flex" aria-hidden="true">
            {iconRight}
          </span>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
